type AnalyticsProperties = Record<string, unknown>;

interface AnalyticsEvent {
    eventName: string;
    source: string;
    timestamp: string;
    sessionId: string;
    anonymousId: string;
    properties?: AnalyticsProperties;
}

interface AnalyticsContext {
    organizationId?: string;
    userId?: string;
    appVersion?: string;
}

const ENDPOINT = '/api/analytics/events';
const SOURCE = 'web_portal';
const ANONYMOUS_ID_KEY = 'nx.analytics.anonymousId';
const SESSION_ID_KEY = 'nx.analytics.sessionId';
const MAX_QUEUE_SIZE = 50;
const BATCH_SIZE = 25;
const UUID_PATTERN = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi;
const NUMERIC_ID_PATTERN = /\b\d{4,}\b/g;

let context: AnalyticsContext = {};
let queue: AnalyticsEvent[] = [];
// `number` is what DOM's `window.setTimeout` returns. Prefer an explicit type
// over `ReturnType<typeof window.setTimeout>` because `@types/node` is present
// in this project and causes the generic to resolve to Node's `Timeout`.
let flushTimer: number | undefined;
let inFlight: Promise<void> | null = null;
let _listenersInitialized = false;

export function configureAnalyticsTelemetry(nextContext: AnalyticsContext) {
    context = {
        ...context,
        ...nextContext,
    };
}

export function sanitizePath(path: string): string {
    if (!path) return path;
    return path
        .replace(UUID_PATTERN, ':id')
        .replace(NUMERIC_ID_PATTERN, ':id');
}

export function trackAnalyticsPageView(path: string, properties: AnalyticsProperties = {}) {
    initListeners();
    trackAnalyticsEvent('page_view', {
        path: sanitizePath(path),
        title: document.title,
        ...properties,
    });
}

export function trackAnalyticsEvent(eventName: string, properties: AnalyticsProperties = {}) {
    if (!eventName) return;

    initListeners();

    queue.push({
        eventName,
        source: SOURCE,
        timestamp: new Date().toISOString(),
        sessionId: getSessionId(),
        anonymousId: getAnonymousId(),
        properties: {
            ...context,
            ...properties,
        },
    });

    scheduleFlush();
}

export function flushAnalyticsTelemetry(useBeacon = false): Promise<void> {
    if (inFlight) return inFlight;
    if (queue.length === 0) return Promise.resolve();

    const events = queue.splice(0, BATCH_SIZE);
    const payload = JSON.stringify({ events });

    if (useBeacon && typeof navigator !== 'undefined' && navigator.sendBeacon) {
        const accepted = navigator.sendBeacon(
            ENDPOINT,
            new Blob([payload], { type: 'application/json' }),
        );
        if (accepted) {
            if (queue.length > 0) scheduleFlush();
            return Promise.resolve();
        }
    }

    const flushPromise = fetch(ENDPOINT, {
        method: 'POST',
        credentials: 'include',
        keepalive: true,
        headers: {
            'Content-Type': 'application/json',
        },
        body: payload,
    }).then((response) => {
        if (response && response.status === 401) {
            // Unauthenticated: drop everything to avoid leaking events / infinite retries.
            queue = [];
            return;
        }
        if (response && !response.ok && response.status >= 500) {
            requeueWithCap(events);
        }
    }).catch(() => {
        requeueWithCap(events);
    }).finally(() => {
        inFlight = null;
        if (queue.length > 0) scheduleFlush();
    });

    inFlight = flushPromise;
    return flushPromise;
}

function requeueWithCap(events: AnalyticsEvent[]) {
    queue = events.concat(queue);
    if (queue.length > MAX_QUEUE_SIZE) {
        const dropped = queue.length - MAX_QUEUE_SIZE;
        // Keep newest, drop oldest.
        queue = queue.slice(queue.length - MAX_QUEUE_SIZE);
        // eslint-disable-next-line no-console
        console.warn(`[analytics] queue overflow, dropped ${dropped} events`);
    }
}

function scheduleFlush() {
    if (flushTimer !== undefined) return;
    if (typeof window === 'undefined') return;

    flushTimer = window.setTimeout(() => {
        flushTimer = undefined;
        void flushAnalyticsTelemetry();
    }, 1000);
}

function getAnonymousId() {
    return getOrCreateId(localStorage, ANONYMOUS_ID_KEY);
}

function getSessionId() {
    return getOrCreateId(sessionStorage, SESSION_ID_KEY);
}

function getOrCreateId(storage: Storage, key: string) {
    try {
        const existing = storage.getItem(key);
        if (existing) return existing;

        const next = createId();
        storage.setItem(key, next);
        return next;
    } catch {
        return createId();
    }
}

function createId() {
    if (typeof window !== 'undefined' && window.crypto?.randomUUID) {
        return window.crypto.randomUUID();
    }

    return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function initListeners() {
    if (_listenersInitialized) return;
    if (typeof window === 'undefined') return;
    _listenersInitialized = true;

    const flushOnHide = () => {
        if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
            void flushAnalyticsTelemetry(true);
        }
    };
    window.addEventListener('visibilitychange', flushOnHide);
    window.addEventListener('pagehide', () => {
        void flushAnalyticsTelemetry(true);
    });
}

// Initialize listeners eagerly when running in a browser. Tests/SSR will lazily
// initialize on the first track call.
if (typeof window !== 'undefined') {
    initListeners();
}

// Test-only reset to allow isolating module state between test cases.
export function __resetAnalyticsTelemetryForTests() {
    if (flushTimer !== undefined && typeof window !== 'undefined') {
        window.clearTimeout(flushTimer);
    }
    context = {};
    queue = [];
    flushTimer = undefined;
    inFlight = null;
    _listenersInitialized = false;
}
